import React from 'react';
import T from 'Components/Helpers/translate';
import { getInitials } from 'App/helpers/getInitials';
import AppService from 'App/legacy/AppService';
import { cn } from 'App/shadcn/lib/utils';
import { numberFormat } from 'App/babel/components/Filters/Currencies';
import { Button } from 'App/shadcn/ui/button';
import { CARD, PILL, ValueRow } from './LookupParts';
import type { LookupCompany } from './lookupApi';
import CompanyPeople from './CompanyPeople';
import { useSoftDeployAccess } from 'App/components/hooks';

type Props = { company: LookupCompany; onLookUp: (query: string) => void };

const CompanyCard = ({ company, onLookUp }: Props) => {
	// "People at this company" runs on Airscale (WEB-13377); the server has the last word on access.
	const hasAirscale = useSoftDeployAccess('AIRSCALE_INTEGRATION');
	const { firmographics: facts, crm } = company;
	const country = facts?.country ?? company.countryCode;
	const place = [...company.sectors.slice(0, 2), facts?.city, country].filter((part): part is string => !!part);
	const rows: Array<{ label: string; value: string; href?: string }> = [
		...(company.orgNumber ? [{ label: T('default.orgNumber'), value: company.orgNumber }] : []),
		...(company.domain
			? [{ label: T('default.webpage'), value: company.domain, href: `https://${company.domain}` }]
			: []),
		...(facts?.employees != null ? [{ label: T('default.employees'), value: numberFormat(facts.employees) }] : []),
		...(facts?.revenue != null ? [{ label: T('account.revenue'), value: numberFormat(facts.revenue) }] : []),
		...(facts?.foundedYear != null ? [{ label: T('default.founded'), value: String(facts.foundedYear) }] : [])
	];

	return (
		<section className={CARD} aria-label={company.name}>
			<div className="flex flex-wrap items-center gap-[14px] border-b border-grey-4 p-5">
				<div
					aria-hidden
					className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] bg-grey-3 text-headline-xs text-brand"
				>
					{getInitials(company).toUpperCase()}
				</div>
				<div className="min-w-0 flex-[1_1_220px]">
					<h2 className="text-headline-xs text-brand">{company.name}</h2>
					{place.length ? (
						<p className="flex flex-wrap gap-x-1 text-body-md text-grey-11">
							{place.map((part, index) => (
								<React.Fragment key={`${index}-${part}`}>
									{index ? <span aria-hidden>{'·'}</span> : null}
									<span>{part}</span>
								</React.Fragment>
							))}
						</p>
					) : null}
				</div>
				<span
					className={cn(PILL, crm ? 'bg-success-light text-success-dark' : 'bg-alert-light text-alert-dark')}
				>
					{crm ? T('prospectingAgent.crmAlreadyIn') : T('prospectingAgent.crmNotYet')}
				</span>
			</div>
			{crm ? (
				<div className="flex flex-wrap gap-2 border-b border-grey-4 px-5 py-4">
					<Button variant="outline" asChild>
						<a href={`/#/${AppService.getCustomerId()}/accounts/${crm.clientId}/`}>
							{T('prospectingAgent.crmOpenIn')}
						</a>
					</Button>
				</div>
			) : null}
			{rows.length ? (
				<dl className="m-0">
					{rows.map(row => (
						<ValueRow key={row.label} label={row.label} value={row.value} href={row.href} />
					))}
				</dl>
			) : null}
			{company.decisionMakers.length ? (
				<div className="flex flex-col">
					<h3 className="px-5 pb-2 pt-4 text-body-md font-semibold text-brand">
						{T('prospectingAgent.decisionMakers')}
					</h3>
					<div className="overflow-x-auto">
						<table className="w-full border-collapse text-body-md">
							<thead>
								<tr className="text-left text-body-sm text-grey-11">
									<th scope="col" className="px-5 py-2 font-medium">
										{T('default.name')}
									</th>
									<th scope="col" className="px-2 py-2 font-medium">
										{T('default.role')}
									</th>
									<th scope="col" className="px-5 py-2">
										<span className="sr-only">
											{T('prospectingTools.lookup.findContactDetails')}
										</span>
									</th>
								</tr>
							</thead>
							<tbody>
								{company.decisionMakers.map(person => (
									<tr key={`${person.name}-${person.role ?? ''}`} className="border-t border-grey-2">
										<td className="px-5 py-3 font-medium text-grey-13">{person.name}</td>
										<td className="px-2 py-3 text-grey-11">{person.role}</td>
										<td className="px-5 py-3 text-right">
											<Button
												type="button"
												variant="outline"
												size="sm"
												// "Name, company" is a query Lookup recognises as a person.
												onClick={() => onLookUp(`${person.name}, ${company.name}`)}
											>
												{T('prospectingTools.lookup.findContactDetails')}
											</Button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			) : null}
			{hasAirscale && company.domain ? <CompanyPeople domain={company.domain} onLookUp={onLookUp} /> : null}
		</section>
	);
};

export default CompanyCard;
